import { useState, useEffect } from 'react';
import './category-report.css';
import { reportsAPI } from '../../../config/reports-api';
import { ChevronDown, ChevronRight, Share2 } from 'lucide-react';
import { toast } from 'react-toastify';

const CategoryReport = () => {
  const today = new Date().toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const formatDisplayDate = (dateValue) => {
    const date = new Date(dateValue);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${date.getFullYear()}`;
  };

  const formatAmount = (value) => {
    const amount = Number(value) || 0;
    return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const capitalize = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const fetchReport = async (from, to) => {
    const start = new Date(from);
    start.setHours(0, 0, 0, 0);
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);

    try {
      setLoading(true);
      setError(null);

      const result = await reportsAPI.getCategoryReport(start.toISOString(), end.toISOString());

      if (result.success) {
        setReport({ list: result.list, summary: result.summary, from, to });
        setExpandedCategories({});
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error fetching category report: ' + err.message);
      console.error('Error fetching category report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(today, today);
  }, []);

  const handleLoadReport = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }
    fetchReport(startDate, endDate);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const shareToWhatsApp = (title, count, total) => {
    const range = `${formatDisplayDate(report.from)} - ${formatDisplayDate(report.to)}`;
    const message = `*${title} Sale Report ${range}*\n\nTotal Items Sold: ${count}\nTotal Amount: QR ${formatAmount(total)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const categories = report?.list || [];
  const itemsSold = categories.reduce((sum, item) => sum + (Number(item.count) || 0), 0);

  return (
    <div className="cat-report-page">
      <div className="cat-report-header">
        <h1>Category Wise Report</h1>
        <p>Quantity sold and sale value per category for delivered orders</p>
      </div>

      <div className="cat-report-filters">
        <div className="cat-report-date-group">
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="cat-report-date-input"
            max={today}
          />
        </div>
        <div className="cat-report-date-group">
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="cat-report-date-input"
            max={today}
          />
        </div>
        <button className="cat-report-load-btn" onClick={handleLoadReport} disabled={loading}>
          {loading ? 'Loading...' : 'Load Report'}
        </button>
      </div>

      {loading ? (
        <div className="cat-report-state">
          <div className="cat-report-spinner"></div>
          <p>Loading report data...</p>
        </div>
      ) : error ? (
        <div className="cat-report-state">
          <p>Error: {error}</p>
          <button className="cat-report-retry-btn" onClick={() => fetchReport(startDate, endDate)}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="cat-report-summary">
            <div className="cat-report-stat-card" style={{ backgroundColor: '#8b5cf6' }}>
              <div className="cat-report-stat-value">{report?.summary?.deliveredOrderCount || 0}</div>
              <div className="cat-report-stat-label">Delivered Orders</div>
            </div>
            <div className="cat-report-stat-card" style={{ backgroundColor: '#3b82f6' }}>
              <div className="cat-report-stat-value">QR {formatAmount(report?.summary?.totalOrderAmount)}</div>
              <div className="cat-report-stat-label">Item Total</div>
            </div>
            <div className="cat-report-stat-card" style={{ backgroundColor: '#10b981' }}>
              <div className="cat-report-stat-value">{itemsSold}</div>
              <div className="cat-report-stat-label">Products Sold</div>
            </div>
            <div className="cat-report-stat-card" style={{ backgroundColor: '#f59e0b' }}>
              <div className="cat-report-stat-value">{categories.length}</div>
              <div className="cat-report-stat-label">Categories</div>
            </div>
          </div>

          {categories.length === 0 ? (
            <div className="cat-report-state">
              <p>No delivered orders found in this date range</p>
            </div>
          ) : (
            <div className="cat-report-table-wrapper">
              <table className="cat-report-table">
                <thead>
                  <tr>
                    <th className="cat-report-index-col">#</th>
                    <th>Category</th>
                    <th className="cat-report-number-col">Products Sold</th>
                    <th className="cat-report-number-col">Total</th>
                    <th className="cat-report-action-col"></th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((item, index) => {
                    const subCategories = (item.subCategories || []).filter(
                      (sub) => sub.subCategory && String(sub.subCategory).trim()
                    );
                    const hasSubCategories = subCategories.length > 0;
                    const isExpanded = !!expandedCategories[item.category];
                    const categoryName = item.category ? capitalize(item.category) : 'Uncategorized';

                    return [
                      <tr key={item.category} className="cat-report-category-row">
                        <td className="cat-report-index-col">{index + 1}</td>
                        <td>
                          {hasSubCategories ? (
                            <button
                              type="button"
                              className="cat-report-expand-btn"
                              onClick={() => toggleCategory(item.category)}
                              title="Show sub categories"
                            >
                              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              <span>{categoryName}</span>
                            </button>
                          ) : (
                            <span className="cat-report-expand-btn cat-report-expand-btn--static">
                              <span>{categoryName}</span>
                            </span>
                          )}
                        </td>
                        <td className="cat-report-number-col">{item.count}</td>
                        <td className="cat-report-number-col">QR {formatAmount(item.total)}</td>
                        <td className="cat-report-action-col">
                          <button
                            className="cat-report-share-btn"
                            onClick={() => shareToWhatsApp(categoryName, item.count, item.total)}
                            title="Share on WhatsApp"
                          >
                            <Share2 size={16} />
                          </button>
                        </td>
                      </tr>,
                      ...(isExpanded && hasSubCategories ? subCategories.map((sub) => {
                        const subName = capitalize(sub.subCategory);

                        return (
                          <tr key={`${item.category}-${sub.subCategory}`} className="cat-report-sub-row">
                            <td className="cat-report-index-col"></td>
                            <td className="cat-report-sub-name">{subName}</td>
                            <td className="cat-report-number-col">{sub.count}</td>
                            <td className="cat-report-number-col">QR {formatAmount(sub.total)}</td>
                            <td className="cat-report-action-col">
                              <button
                                className="cat-report-share-btn"
                                onClick={() => shareToWhatsApp(`${categoryName} - ${subName}`, sub.count, sub.total)}
                                title="Share on WhatsApp"
                              >
                                <Share2 size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      }) : [])
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CategoryReport;
